import React from 'react';
import PropTypes from 'prop-types';
import { UX2 } from '@wsb/guac-widget-core';
import dataAids from '../../common/constants/dataAids';
import ReamazeApi from '../../clients/ReamazeApi';
import ViewArticle from './ViewArticle';
import {
  parseFaqPath,
  buildArticlePath,
  buildTopicPath,
  navigateToUrl,
  getPagePath,
  MISC_TOPIC_SLUG
} from '../../common/helpers/faqPathHelper';

class FAQLayout2 extends React.Component {
  static propTypes = {
    ventureId: PropTypes.string,
    websiteId: PropTypes.string.isRequired,
    isInternalPage: PropTypes.bool,
    order: PropTypes.number,
    sectionTitle: PropTypes.string,
    sectionDescription: PropTypes.string,
    miscTopicName: PropTypes.string,
    allTopicsLabel: PropTypes.string,
    loadingArticlesLabel: PropTypes.string,
    loadingArticleLabel: PropTypes.string,
    noArticlesFoundLabel: PropTypes.string,
    noTopicArticlesFoundLabel: PropTypes.string,
    articleNotFoundLabel: PropTypes.string,
    viewAllArticlesLabel: PropTypes.string,
    renderMode: PropTypes.string
  };

  constructor(props) {
    super(props);
    this.state = {
      articles: [],
      loading: true,
      error: null,
      currentView: 'list', // 'list', 'topic', or 'article'
      selectedTopicSlug: null,
      selectedArticleSlug: null
    };
    this.handleHashChange = this.handleHashChange.bind(this);
    this.handleBackToList = this.handleBackToList.bind(this);
    this.handleTopicClick = this.handleTopicClick.bind(this);
    this.handleArticleClick = this.handleArticleClick.bind(this);
  }

  /**
   * Fetches articles from the Reamaze API
   * @param {string} brandId - The brand ID
   * @param {string} websiteId - The website ID
   * @param {Object} params - Query parameters for filtering articles
   * @returns {Promise} Promise resolving to articles data
   */
  fetchArticles(brandId, websiteId, params = {}) {
    // Validate brandId
    if (!brandId || typeof brandId !== 'string') {
      console.error(`Invalid brandId: ${brandId}`);
      this.setState({
        error: 'Unable to load articles. Please try again later.',
        loading: false
      });
      return Promise.reject(new Error('Invalid brand ID'));
    }

    // Detect environment and construct API host - always use godaddy.com domains
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    let domainSuffix = 'godaddy.com'; // Production default

    // Detect environment based on hostname patterns
    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      // Local development - use dev-godaddy.com
      domainSuffix = 'dev-godaddy.com';
    } else if (
      hostname.includes('test-godaddy.com') ||
      hostname.includes('test.godaddysites.com')
    ) {
      // GoDaddy test environment
      domainSuffix = 'test-godaddy.com';
    } else if (hostname.includes('dev-godaddy.com') || hostname.includes('dev.godaddysites.com')) {
      // GoDaddy dev environment
      domainSuffix = 'dev-godaddy.com';
    }
    // All other cases use production: godaddy.com

    // Construct API host dynamically with brandId already included
    const reamazeApiHost = `https://${brandId}.reamaze.${domainSuffix}`;
    const reamazeApi = new ReamazeApi(reamazeApiHost);

    return reamazeApi
      .listArticles(brandId, websiteId, params)
      .then(response => {
        this.setState({
          articles: response.articles || [],
          loading: false
        });
        return response;
      })
      .catch(err => {
        console.error(`Error fetching articles: ${err.message || err}`);
        this.setState({
          error: 'Unable to load articles. Please try again later.',
          loading: false
        });
        throw err;
      });
  }

  /**
   * Filter out unlisted articles (visibility === 2)
   * @param {Array} articles - Array of articles to filter
   * @returns {Array} Filtered articles without unlisted ones
   */
  filterUnlistedArticles(articles) {
    return articles.filter(article => {
      // Filter out unlisted articles (visibility === 2)
      // visibility: public(0), internal(1), unlisted(2)
      return article.visibility !== 2;
    });
  }

  /**
   * Groups articles by their topic/category
   * Filters out unlisted articles from list view
   * @returns {Object} Articles grouped by topic name
   */
  groupArticlesByCategory() {
    const { articles } = this.state;
    // Filter out unlisted articles for list view
    const filteredArticles = this.filterUnlistedArticles(articles);
    const grouped = {};

    filteredArticles.forEach(article => {
      const categoryName = article.topic?.name || this.props.miscTopicName;
      if (!grouped[categoryName]) {
        grouped[categoryName] = [];
      }
      grouped[categoryName].push(article);
    });

    return grouped;
  }

  componentDidMount() {
    // Parse URL first to set the correct view before fetching
    this.updateViewFromUrl();

    // Fetch articles when component mounts
    const { ventureId, websiteId } = this.props;

    // Check if ventureId is valid
    if (!ventureId || typeof ventureId !== 'string') {
      console.error(`ventureId is not valid: ${ventureId}`);
      this.setState({
        error: 'Unable to load articles. Please try again later.',
        loading: false
      });
      return;
    }

    // Use websiteId if available, otherwise fall back to ventureId
    const websiteIdToUse = websiteId || ventureId;
    this.fetchArticles(ventureId, websiteIdToUse, {
      status: 'published',
      page: 1,
      count_per_page: 30
    }).catch(err => {
      console.error(`Failed to fetch articles: ${err.message || err}`);
      this.setState({
        error: 'Unable to load articles. Please try again later.',
        loading: false
      });
    });

    // Listen to hash changes for navigation
    window.addEventListener('hashchange', this.handleHashChange);
  }

  componentWillUnmount() {
    // Clean up event listener
    window.removeEventListener('hashchange', this.handleHashChange);
  }

  /**
   * Handle hash change navigation
   */
  handleHashChange() {
    this.updateViewFromUrl();
  }

  /**
   * Parse URL and update the current view state
   */
  updateViewFromUrl() {
    const { topicSlug, articleSlug } = parseFaqPath();

    if (articleSlug && topicSlug) {
      // Show single article view
      this.setState({
        currentView: 'article',
        selectedTopicSlug: topicSlug,
        selectedArticleSlug: articleSlug
      });
    } else if (topicSlug) {
      // Show topic filtered view
      this.setState({
        currentView: 'topic',
        selectedTopicSlug: topicSlug,
        selectedArticleSlug: null
      });
    } else {
      // Show all articles view
      this.setState({
        currentView: 'list',
        selectedTopicSlug: null,
        selectedArticleSlug: null
      });
    }
  }

  /**
   * Navigate to view all articles
   */
  handleBackToList() {
    const url = getPagePath();
    navigateToUrl(url);
    this.setState({
      currentView: 'list',
      selectedTopicSlug: null,
      selectedArticleSlug: null
    });
  }

  /**
   * Navigate to a specific topic view
   * @param {string} topicSlug - The topic slug
   * @param {Event} e - The click event
   */
  handleTopicClick(topicSlug, e) {
    if (e) {
      e.preventDefault();
    }
    const effectiveTopicSlug = topicSlug || MISC_TOPIC_SLUG;
    const url = buildTopicPath(effectiveTopicSlug);
    navigateToUrl(url);
    this.setState({
      currentView: 'topic',
      selectedTopicSlug: effectiveTopicSlug,
      selectedArticleSlug: null
    });
  }

  /**
   * Navigate to a specific article view
   * @param {Object} article - The article object with slug and topic
   * @param {Event} e - The click event
   */
  handleArticleClick(article, e) {
    if (e) {
      e.preventDefault();
    }
    const topicSlug = article.topic?.slug || MISC_TOPIC_SLUG;
    const articleSlug = article.slug;

    if (!articleSlug) {
      return;
    }

    const url = buildArticlePath(topicSlug, articleSlug);
    navigateToUrl(url);
    this.setState({
      currentView: 'article',
      selectedTopicSlug: topicSlug,
      selectedArticleSlug: articleSlug
    });
  }

  renderIntro() {
    const { isInternalPage, order, sectionTitle, sectionDescription } = this.props;
    if (!sectionTitle && !sectionDescription) {
      return null;
    }

    const sectionHeadingProps = {
      children: sectionTitle,
      tag: 'h2',
      ['data-aid']: dataAids.FAQ_SECTION_TITLE_RENDERED,
      ['data-route']: 'sectionTitle',
      isInternalPage,
      order
    };
    const descriptionProps = {
      children: sectionDescription,
      ['data-aid']: dataAids.FAQ_SECTION_DESCRIPTION_RENDERED,
      ['data-route']: 'sectionDescription',
      richtext: true
    };

    return <UX2.Component.Intro heading={ sectionHeadingProps } text={ descriptionProps } />;
  }

  /**
   * Render a single topic's articles (when viewing a specific topic)
   * @returns {Object} JSX element for topic view
   */
  renderTopicView() {
    const { articles, selectedTopicSlug } = this.state;

    // Find articles for this topic and filter out unlisted articles
    // If selectedTopicSlug is MISC_TOPIC_SLUG, filter for articles without a topic
    const topicArticles = this.filterUnlistedArticles(
      articles.filter(article => {
        if (selectedTopicSlug === MISC_TOPIC_SLUG) {
          return !article.topic;
        }
        return article.topic?.slug === selectedTopicSlug;
      })
    );

    const { noTopicArticlesFoundLabel } = this.props;
    if (topicArticles.length === 0) {
      return (
        <UX2.Element.Block>
          <UX2.Element.Text>{ noTopicArticlesFoundLabel }</UX2.Element.Text>
        </UX2.Element.Block>
      );
    }

    const topicName = topicArticles[0].topic?.name || this.props.miscTopicName;
    const { allTopicsLabel } = this.props;

    const styles = {
      container: {
        maxWidth: '800px',
        margin: '0 auto'
      },
      backLink: {
        marginBottom: 'xlarge'
      },
      heading: {
        marginBottom: 'large'
      },
      articleList: {
        paddingLeft: 0,
        listStyle: 'none'
      },
      articleItem: {
        paddingVertical: 'small',
        borderBottom: '1px solid',
        borderColor: 'input'
      },
      articleLink: {
        textDecoration: 'none',
        display: 'inline-block',
        color: 'highlight',
        fontSize: 'large'
      }
    };

    return (
      <UX2.Element.Block style={ styles.container }>
        <UX2.Element.MoreLink.Backward
          style={ styles.backLink }
          href={ getPagePath() }
          onClick={ e => {
            e.preventDefault();
            this.handleBackToList();
          } }
          data-aid={ dataAids.FAQ_BACK_LINK }
        >
          { allTopicsLabel }
        </UX2.Element.MoreLink.Backward>

        <UX2.Element.Heading.Product tag='h2' featured style={ styles.heading }>
          { topicName }
        </UX2.Element.Heading.Product>

        <UX2.Element.Block tag='ul' style={ styles.articleList }>
          { topicArticles.map((article, index) => (
            <UX2.Element.Block key={ article.slug || article.id } tag='li' style={ styles.articleItem }>
              <UX2.Element.Link
                href={ buildArticlePath(article.topic?.slug, article.slug) }
                style={ styles.articleLink }
                data-aid={ `${dataAids.FAQ_ANSWER_RENDERED}_topic_${index}` }
                onClick={ e => this.handleArticleClick(article, e) }
              >
                { article.title }
              </UX2.Element.Link>
            </UX2.Element.Block>
          )) }
        </UX2.Element.Block>
      </UX2.Element.Block>
    );
  }

  renderArticles() {
    const { loading, error } = this.state;
    const groupedArticles = this.groupArticlesByCategory();
    const categories = Object.keys(groupedArticles).sort();
    const MAX_ARTICLES_TO_SHOW = 5;
    const { loadingArticlesLabel, noArticlesFoundLabel, viewAllArticlesLabel } = this.props;

    const styles = {
      grid: {
        'display': 'grid',
        'gridTemplateColumns': '1fr',
        'gap': 0,
        '@media (min-width: 1200px)': {
          gridTemplateColumns: 'repeat(2, 1fr)',
          columnGap: 'xlarge'
        }
      },
      column: {
        display: 'flex',
        flexDirection: 'column'
      },
      categoryBlock: {
        paddingVertical: 'large',
        breakInside: 'avoid'
      },
      categoryHeading: {
        paddingBottom: 'small'
      },
      categoryHeadingLink: {
        'textDecoration': 'none',
        'color': 'inherit',
        'cursor': 'pointer',
        ':hover': {
          textDecoration: 'underline'
        }
      },
      articleList: {
        paddingLeft: 0,
        listStyle: 'none'
      },
      articleItem: {
        paddingVertical: 'xsmall'
      },
      articleLink: {
        textDecoration: 'none',
        display: 'inline-block',
        color: 'highlight'
      },
      viewAllLink: {
        paddingTop: 'small',
        textAlign: 'left'
      },
      viewAllLinkText: {
        fontWeight: 'bold',
        textDecoration: 'underline'
      }
    };

    if (loading) {
      return (
        <UX2.Element.Block>
          <UX2.Element.Text>{ loadingArticlesLabel }</UX2.Element.Text>
        </UX2.Element.Block>
      );
    }

    if (error) {
      return (
        <UX2.Element.Block>
          <UX2.Element.Text>{ error }</UX2.Element.Text>
        </UX2.Element.Block>
      );
    }

    if (categories.length === 0) {
      return (
        <UX2.Element.Block>
          <UX2.Element.Text>{ noArticlesFoundLabel }</UX2.Element.Text>
        </UX2.Element.Block>
      );
    }

    // Split categories into two columns for larger screens
    const midpoint = Math.ceil(categories.length / 2);
    const leftColumnCategories = categories.slice(0, midpoint);
    const rightColumnCategories = categories.slice(midpoint);

    const renderCategoryColumn = (columnCategories, columnOffset = 0) => (
      <UX2.Element.Block style={ styles.column }>
        { columnCategories.map((category, index) => {
          const categoryIndex = index + columnOffset;
          const articles = groupedArticles[category];
          const totalArticles = articles.length;
          const articlesToShow = articles.slice(0, MAX_ARTICLES_TO_SHOW);
          const hasMore = totalArticles > MAX_ARTICLES_TO_SHOW;

          return (
            <UX2.Element.Block key={ category } style={ styles.categoryBlock }>
              <UX2.Element.Heading.Product tag='h3' featured style={ styles.categoryHeading }>
                <UX2.Element.Link
                  href={ buildTopicPath(articles[0].topic?.slug) }
                  style={ styles.categoryHeadingLink }
                  data-aid={ `FAQ_TOPIC_HEADING_${categoryIndex}` }
                  onClick={ e => this.handleTopicClick(articles[0].topic?.slug, e) }
                >
                  { category }
                </UX2.Element.Link>
              </UX2.Element.Heading.Product>
              <UX2.Element.Block tag='ul' style={ styles.articleList }>
                { articlesToShow.map((article, articleIndex) => (
                  <UX2.Element.Block
                    key={ article.slug || article.id }
                    tag='li'
                    style={ styles.articleItem }
                  >
                    <UX2.Element.Link
                      href={ buildArticlePath(article.topic?.slug, article.slug) }
                      style={ styles.articleLink }
                      data-aid={ `${dataAids.FAQ_ANSWER_RENDERED}_${categoryIndex}_${articleIndex}` }
                      onClick={ e => this.handleArticleClick(article, e) }
                    >
                      { article.title }
                    </UX2.Element.Link>
                  </UX2.Element.Block>
                )) }
              </UX2.Element.Block>
              { hasMore && (
                <UX2.Element.Block style={ styles.viewAllLink } category='accent'>
                  <UX2.Element.Link
                    href={ buildTopicPath(articles[0].topic?.slug) }
                    style={ styles.viewAllLinkText }
                    data-aid={ `FAQ_VIEW_ALL_${categoryIndex}` }
                    onClick={ e => this.handleTopicClick(articles[0].topic?.slug, e) }
                  >
                    <UX2.Element.Text color='section' style={ styles.viewAllLinkText }>
                      { viewAllArticlesLabel.replace('{count}', totalArticles) } →
                    </UX2.Element.Text>
                  </UX2.Element.Link>
                </UX2.Element.Block>
              ) }
            </UX2.Element.Block>
          );
        }) }
      </UX2.Element.Block>
    );

    return (
      <UX2.Element.Block style={ styles.grid }>
        { renderCategoryColumn(leftColumnCategories, 0) }
        { renderCategoryColumn(rightColumnCategories, midpoint) }
      </UX2.Element.Block>
    );
  }

  render() {
    const { currentView, articles, selectedArticleSlug, loading } = this.state;
    const { allTopicsLabel, loadingArticleLabel, articleNotFoundLabel } = this.props;

    const containerStyles = {
      maxWidth: '100%',
      ['@xs']: {
        maxWidth: '100%'
      },
      ['@sm']: {
        maxWidth: '100%'
      },
      ['@md']: {
        maxWidth: '1400px'
      }
    };

    // Find the selected article if we're in article view
    const selectedArticle =
      currentView === 'article' ? articles.find(a => a.slug === selectedArticleSlug) : null;

    return (
      <UX2.Component.Widget { ...this.props }>
        <UX2.Group.Section>
          <UX2.Element.Container style={ containerStyles }>
            { currentView === 'list' && this.renderIntro() }

            { currentView === 'list' && (
              <UX2.Component.Grid bottom={ false } inset={ true } group='group'>
                { this.renderArticles() }
              </UX2.Component.Grid>
            ) }

            { currentView === 'topic' && this.renderTopicView() }

            { currentView === 'article' && selectedArticle && (
              <ViewArticle
                article={ selectedArticle }
                backClickHandler={ () => this.handleBackToList() }
                staticContent={{ allArticles: allTopicsLabel }}
                miscTopicName={ this.props.miscTopicName }
              />
            ) }

            { currentView === 'article' && !selectedArticle && loading && (
              <UX2.Element.Block>
                <UX2.Element.Text>{ loadingArticleLabel }</UX2.Element.Text>
              </UX2.Element.Block>
            ) }

            { currentView === 'article' && !selectedArticle && !loading && (
              <UX2.Element.Block>
                <UX2.Element.Text>{ articleNotFoundLabel }</UX2.Element.Text>
              </UX2.Element.Block>
            ) }
          </UX2.Element.Container>
        </UX2.Group.Section>
      </UX2.Component.Widget>
    );
  }
}

export default FAQLayout2;
